import { create } from 'zustand';
import type { PickList, PickItem, ScanResult, PickingState } from '../types';
import { pickListService, pickItemService } from '../services/supabase';

interface PickingStore extends PickingState {
  // Actions
  setCurrentPickList: (pickList: PickList | null) => void;
  loadPickList: (id: string) => Promise<void>;
  loadUserPickLists: (userId: string) => Promise<PickList[]>;
  updatePickItem: (id: string, updates: Partial<PickItem>) => Promise<void>;
  completePickItem: (id: string, quantityPicked: number) => Promise<void>;
  completePickList: () => Promise<void>;
  startScanning: () => void;
  stopScanning: () => void;
  setScanResult: (result: ScanResult | null) => void;
  processScan: (barcode: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export const usePickingStore = create<PickingStore>((set, get) => ({
  currentPickList: null,
  pickItems: [],
  scanning: false,
  scanResult: null,
  isLoading: false,
  error: null,

  setCurrentPickList: (pickList) => {
    set({ currentPickList: pickList });
    if (pickList) {
      get().loadPickList(pickList.id);
    }
  },

  loadPickList: async (id: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const { data, error } = await pickListService.getById(id);
      
      if (error) {
        set({ error: error.message, isLoading: false });
        return;
      }
      
      if (data) {
        // Transform the data to match our types
        const pickList: PickList = {
          id: data.id,
          orderNumber: data.order_number,
          status: data.status,
          assignedTo: data.assigned_to,
          priority: data.priority,
          customerName: data.customer_name,
          totalItems: data.total_items,
          pickedItems: data.picked_items,
          estimatedTime: data.estimated_time,
          actualTime: data.actual_time,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
          dueDate: data.due_date,
          pickingType: 'individual'
        };

        const pickItems: PickItem[] = (data.pick_items || []).map((item: any) => ({
          id: item.id,
          pickListId: item.pick_list_id,
          productSku: item.product_sku,
          productName: item.product_name,
          quantityRequired: item.quantity_required,
          quantityPicked: item.quantity_picked,
          binLocation: item.bin_location,
          status: item.status,
          barcode: item.barcode,
          notes: item.notes,
          createdAt: item.created_at,
          updatedAt: item.updated_at
        }));
        
        set({ 
          currentPickList: pickList,
          pickItems,
          isLoading: false 
        });
      }
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to load pick list',
        isLoading: false 
      });
    }
  },

  loadUserPickLists: async (userId: string) => {
    set({ isLoading: true, error: null });
    console.log('🏪 Store: Loading pick lists for user:', userId);
    
    try {
      const { data, error } = await pickListService.getByUser(userId);
      
      console.log('🏪 Store: Raw database response:', { 
        dataCount: data?.length, 
        error: error?.message,
        sampleData: data?.slice(0, 2),
        allData: data
      });
      
      if (error) {
        console.error('🏪 Store: Error loading pick lists:', error);
        set({ error: error.message, isLoading: false });
        return [];
      }
      
      // Transform the data to match our types
      const pickLists: PickList[] = (data || []).map((item: any) => ({
        id: item.id,
        orderNumber: item.order_number,
        status: item.status,
        assignedTo: item.assigned_to || userId,
        priority: item.priority,
        customerName: item.customer_name,
        totalItems: item.total_items,
        pickedItems: item.picked_items,
        estimatedTime: item.estimated_time,
        actualTime: item.actual_time,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        dueDate: item.due_date,
        pickingType: 'individual',
        temperatureZone: item.temperature_zone
      }));
      
      console.log('🏪 Store: Transformed pick lists:', {
        count: pickLists.length,
        pickLists: pickLists.map(pl => ({
          id: pl.id,
          orderNumber: pl.orderNumber,
          status: pl.status,
          assignedTo: pl.assignedTo,
          customerName: pl.customerName,
          totalItems: pl.totalItems,
          pickedItems: pl.pickedItems
        }))
      });
      
      set({ isLoading: false });
      return pickLists;
    } catch (error) {
      console.error('🏪 Store: Exception loading pick lists:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to load pick lists',
        isLoading: false 
      });
      return [];
    }
  },

  updatePickItem: async (id: string, updates: Partial<PickItem>) => {
    set({ isLoading: true, error: null });
    
    // Update local state first for immediate UI feedback
    const state = get();
    const updatedItems = state.pickItems.map(item =>
      item.id === id ? { ...item, ...updates } : item
    );
    
    set({ pickItems: updatedItems });
    
    try {
      const { data, error } = await pickItemService.update(id, updates);
      
      if (error) {
        // Revert local changes on error
        set({ pickItems: state.pickItems, error: error.message, isLoading: false });
        return;
      }
      
      // Update pick list progress
      const pickedCount = updatedItems.filter(item => item.status === 'picked').length;
      const currentPickList = state.currentPickList;
      
      if (currentPickList) {
        const updatedPickList = {
          ...currentPickList,
          pickedItems: pickedCount
        };
        
        // Update pick list in database
        await pickListService.update(currentPickList.id, {
          pickedItems: pickedCount,
          status: pickedCount === updatedItems.length ? 'completed' : 'in_progress'
        });
        
        set({ 
          pickItems: updatedItems,
          currentPickList: updatedPickList,
          isLoading: false 
        });
      }
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to update pick item',
        isLoading: false 
      });
    }
  },

  completePickItem: async (id: string, quantityPicked: number) => {
    const item = get().pickItems.find(item => item.id === id);
    if (!item) return;
    
    const status = quantityPicked >= item.quantityRequired ? 'picked' : 'short';
    
    await get().updatePickItem(id, {
      quantityPicked,
      status
    });
  },

  completePickList: async () => {
    const { currentPickList } = get();
    if (!currentPickList) return;
    
    set({ isLoading: true, error: null });
    
    try {
      await pickListService.update(currentPickList.id, {
        status: 'completed',
        actualTime: Date.now() - new Date(currentPickList.createdAt).getTime()
      });
      
      set({ 
        currentPickList: { ...currentPickList, status: 'completed' },
        isLoading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to complete pick list',
        isLoading: false 
      });
    }
  },

  startScanning: () => set({ scanning: true, scanResult: null }),

  stopScanning: () => set({ scanning: false }),

  setScanResult: (result) => set({ scanResult: result }),

  processScan: async (barcode: string) => {
    const { pickItems } = get();
    
    // Find matching item by barcode or SKU
    const matchingItem = pickItems.find(item => 
      item.barcode === barcode || item.productSku === barcode
    );
    
    if (matchingItem) {
      set({ 
        scanResult: {
          barcode,
          productSku: matchingItem.productSku,
          productName: matchingItem.productName,
          binLocation: matchingItem.binLocation,
          success: true,
          message: 'Item found! Ready to pick.'
        }
      });
    } else {
      set({ 
        scanResult: {
          barcode,
          success: false,
          message: 'Item not found in current pick list.'
        }
      });
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set({
    currentPickList: null,
    pickItems: [],
    scanning: false,
    scanResult: null,
    isLoading: false,
    error: null
  })
}));